import React, { useState, useEffect } from 'react';
import { MapPin, Package, Car, Mail, Users, TrendingUp, Eye, Globe, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardTab = ({ dashboardStats, token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setAnalytics(await res.json());
      } catch (err) {
        console.error('Error fetching analytics:', err);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    if (token) fetchAnalytics();
  }, [token]);

  // Show loading state if dashboardStats not yet loaded
  if (!dashboardStats || !dashboardStats.stats) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-s-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-s-text2">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const maxDaily = analytics?.daily_visitors?.length > 0 
    ? Math.max(...analytics.daily_visitors.map(d => d.count), 1) 
    : 1;

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {/* Overview Stats */}
      <div>
        <h3 className="text-sm font-semibold text-s-muted uppercase tracking-wider mb-3">Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Packages', value: dashboardStats.stats.packages, icon: Package, color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
            { label: 'Fleet Vehicles', value: dashboardStats.stats.fleet, icon: Car, color: 'text-s-primary', bg: 'bg-s-primary/15' },
            { label: 'Pending Enquiries', value: dashboardStats.stats.pending_enquiries, icon: Mail, color: 'text-s-primary', bg: 'bg-s-primary/15', badge: `of ${dashboardStats.stats.enquiries}` },
            { label: 'Team Members', value: dashboardStats.stats.team_members, icon: Users, color: 'text-s-primary', bg: 'bg-s-primary/15' },
          ].map((stat, idx) => (
            <Card key={idx} className="bg-s-bg2 border-s-border" data-testid={`dashboard-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-s-text2 text-xs font-medium">{stat.label}</p>
                    <div className="flex items-baseline gap-1">
                      <p className="text-2xl font-bold text-s-text">{stat.value}</p>
                      {stat.badge && <span className="text-xs text-s-muted">{stat.badge}</span>}
                    </div>
                  </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.bg}`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Visitor Analytics */}
      <div>
        <h3 className="text-sm font-semibold text-s-muted uppercase tracking-wider mb-3">Visitor Analytics</h3>
        {loadingAnalytics ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-s-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : analytics ? (
          <div className="space-y-4">
            {/* Visitor Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Visitors', value: analytics.total_visitors, icon: Users, color: 'text-s-primary', bg: 'bg-s-primary/15' },
                { label: 'Today', value: analytics.today_visitors, icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
                { label: 'Unique Pages', value: analytics.unique_pages, icon: Globe, color: 'text-s-primary', bg: 'bg-s-primary/15' },
                { label: 'Avg/Day', value: analytics.daily_visitors?.length > 0 ? Math.round(analytics.daily_visitors.reduce((s, d) => s + d.count, 0) / analytics.daily_visitors.length) : 0, icon: BarChart3, color: 'text-s-primary', bg: 'bg-s-primary/15' },
              ].map((stat, idx) => (
                <Card key={idx} className="bg-s-bg2 border-s-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-s-text2 text-xs font-medium">{stat.label}</p>
                        <p className="text-2xl font-bold text-s-text">{stat.value.toLocaleString()}</p>
                      </div>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.bg}`}>
                        <stat.icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Daily Visitors Chart */}
            <Card className="bg-s-bg2 border-s-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-s-text text-sm">Daily Visitors (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.daily_visitors?.length === 0 ? (
                  <p className="text-s-text2 text-sm text-center py-6">No visitor data yet.</p>
                ) : (
                  <>
                    <div className="flex items-end gap-1 h-32 mt-2">
                      {analytics.daily_visitors?.map((day, idx) => (
                        <div key={idx} className="flex-1 group relative flex flex-col items-center justify-end h-full">
                          <div
                            className="w-full bg-s-primary/70 hover:bg-s-primary rounded-t transition-all min-h-[2px]"
                            style={{ height: `${Math.max((day.count / maxDaily) * 100, 2)}%` }}
                          />
                          <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-s-bg2 border border-s-border text-s-text text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                            {day.date}: {day.count}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-s-muted mt-1">
                      <span>{analytics.daily_visitors?.[0]?.date}</span>
                      <span>{analytics.daily_visitors?.[analytics.daily_visitors.length - 1]?.date}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Top Pages & Recent Visitors */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-s-bg2 border-s-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-s-text text-sm flex items-center gap-2">
                    <Eye className="w-4 h-4 text-s-primary" /> Top Pages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.top_pages?.length === 0 ? (
                    <p className="text-s-text2 text-sm text-center py-4">No data yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {analytics.top_pages?.slice(0, 6).map((page, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1.5 px-2 bg-s-card/30 rounded text-sm">
                          <span className="text-s-text truncate max-w-[180px]">{page.page || '/'}</span>
                          <span className="text-s-text2 text-xs font-mono">{page.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-s-bg2 border-s-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-s-text text-sm">Recent Enquiries</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dashboardStats.recent_enquiries?.slice(0, 5).map((enq, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-s-card/30 rounded">
                        <div className="min-w-0">
                          <p className="text-s-text text-sm font-medium truncate">{enq.name}</p>
                          <p className="text-s-muted text-xs truncate">{enq.email}</p>
                        </div>
                        <Badge className={
                          enq.status === 'pending' ? 'bg-s-primary/20 text-s-primary' :
                          enq.status === 'contacted' ? 'bg-s-primary/20 text-s-primary' :
                          'bg-s-primary/20 text-s-primary'
                        } style={{ fontSize: '10px' }}>
                          {enq.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <p className="text-s-text2 text-center py-6">Unable to load analytics.</p>
        )}
      </div>
    </div>
  );
};

export default DashboardTab;
